// 2026-06-25: Phase 8b — Scheduler page (Gantt-by-Work-Center)
// Visualises planned operations across all Work Centers on a day-grid.
// Auto-schedule packs un-scheduled PENDING/READY ops into earliest free machine slots.
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  getScheduleGrid,
  getScheduleCapacity,
  autoSchedule,
  unscheduleOperation,
  scheduleOperation,
  listWorkCenters,
} from '../../Services/app/workorderservice';
import { customerbasedshift, cleanCustomerId } from '../../Services/app/operatorservice';
// cleanCustomerId — strips quotes/backslashes from localStorage value
import Swal from 'sweetalert2';
import './Scheduler.css';

const MS_DAY = 24 * 3600_000;
const HOURS_PER_DAY_SHOWN = 16; // 06:00 → 22:00 default view
const PX_PER_HOUR = 36;

// Distinct color per job so overlapping jobs in a cell are easy to tell apart
const JOB_COLORS = ['#2563eb', '#dc2626', '#16a34a', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#65a30d', '#ea580c', '#4f46e5', '#0d9488', '#c026d3'];
function jobColor(key) {
  const s = String(key || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return JOB_COLORS[h % JOB_COLORS.length];
}
// Greedy lane assignment so overlapping jobs stack into separate rows instead of on top of each other
function assignLanes(ops) {
  const laneByOp = new Map();
  const sorted = (ops || []).filter((o) => o.scheduled_start_ts && o.scheduled_end_ts)
    .sort((a, b) => new Date(a.scheduled_start_ts).getTime() - new Date(b.scheduled_start_ts).getTime());
  const laneEnds = [];
  for (const o of sorted) {
    const s = new Date(o.scheduled_start_ts).getTime();
    const e = new Date(o.scheduled_end_ts).getTime();
    let lane = laneEnds.findIndex((end) => end <= s);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(e); } else { laneEnds[lane] = e; }
    laneByOp.set(o.id, lane);
  }
  return { laneByOp, laneCount: Math.max(1, laneEnds.length) };
}

// Render a YYYY-MM-DD in IST without TZ headaches
function istKey(d) {
  const utc = d.getTime();
  const ist = new Date(utc + 5.5 * 3600_000);
  return ist.toISOString().slice(0, 10);
}
function istLabel(d) {
  const ist = new Date(d.getTime() + 5.5 * 3600_000);
  return ist.toISOString().slice(0, 10);
}
function istHour(d) {
  const ist = new Date(d.getTime() + 5.5 * 3600_000);
  return ist.getUTCHours() + ist.getUTCMinutes() / 60;
}

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const ist = new Date(d.getTime() + 5.5 * 3600_000);
  return ist.toISOString().slice(11, 16);
}

export default function Scheduler() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID'));
  const [grid, setGrid] = useState({ work_centers: [], operations: [], window: null });
  const [capacity, setCapacity] = useState({ daily: [] });
  const [processes, setProcesses] = useState([]);
  const [machineGroups, setMachineGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Window: today → +6d default
  const [days, setDays] = useState(7);
  const [includeHolidays, setIncludeHolidays] = useState(false);
  const [hideEmpty, setHideEmpty] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  const startDate = useMemo(() => {
    const d = new Date();
    d.setUTCHours(0, 30, 0, 0); // ~06:00 IST today
    return d;
  }, []);
  const endDate = useMemo(() => new Date(startDate.getTime() + days * MS_DAY), [startDate, days]);

  const [selectedOp, setSelectedOp] = useState(null);
  const [dragOp, setDragOp] = useState(null);
  const [barTip, setBarTip] = useState(null);
  const [pending, setPending] = useState({});  // staged (un-saved) drag/reschedule changes
  const [formMachine, setFormMachine] = useState('');
  const [formStart, setFormStart] = useState('');
  const [formCell, setFormCell] = useState('');
  const [shopFilter, setShopFilter] = useState('ALL');

  const fetchAll = useCallback(async () => {
    if (!customerId) return;
    setPending({});
    setLoading(true);
    try {
      const [g, c, p, mg] = await Promise.all([
        getScheduleGrid(customerId, startDate.toISOString(), endDate.toISOString(), includeHolidays),
        getScheduleCapacity(customerId, startDate.toISOString(), endDate.toISOString(), includeHolidays),
        customerbasedshift(customerId, 'process_master').catch(() => null),
        customerbasedshift(customerId, 'machinegroups').catch(() => null),
      ]);
      setGrid(g || { work_centers: [], operations: [] });
      setCapacity(c || { daily: [] });
      setProcesses(Array.isArray(p?.[0]?.value) ? p[0].value : []);
      setMachineGroups(Array.isArray(mg?.[0]?.value) ? mg[0].value : []);
    } catch (e) {
      console.error('[Scheduler] fetch failed', e);
    }
    setLoading(false);
  }, [customerId, startDate, endDate, includeHolidays]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const runAutoSchedule = async () => {
    setBusy(true);
    try {
      const r = await autoSchedule(customerId, startDate.toISOString());
      Swal.fire({
        icon: 'success', title: 'Auto-schedule complete',
        text: `${r.count} of ${r.total_requested} operations scheduled.`,
        timer: 1800, showConfirmButton: false,
      });
      await fetchAll();
    } catch (e) {
      Swal.fire('Error', e?.response?.data?.message || e.message, 'error');
    }
    setBusy(false);
  };

  const handleDropOnCell = async (e, wc, d) => {
    e.preventDefault();
    if (!dragOp) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    // d.start is already 06:00 IST (grid left edge), matching opStyle's (hours-6) origin,
    // so the drop time is simply d.start + (x / PX_PER_HOUR) hours. (No extra +6.)
    const startMs = d.start.getTime() + Math.max(0, x / PX_PER_HOUR) * 3600000;
    const perPiece = Number(dragOp.planned_time_min || 0);
    const qty = Math.max(1, Number(dragOp.planned_qty || 1));
    const totalMin = perPiece >= 60 ? perPiece : perPiece * qty;
    const endMs = startMs + (totalMin > 0 ? totalMin : 60) * 60000;
    const machines = (wc.machines || []).map((mm) => (typeof mm === 'string' ? mm : mm && mm.name)).filter(Boolean);
    const machine = (wc && wc._rowKind === 'machine' && wc._machineName) ? wc._machineName : (machines[0] || dragOp.scheduled_machine_name || dragOp.machine_name || null);
    const op = dragOp; setDragOp(null);
    setPending((p) => ({ ...p, [op.id]: { scheduled_start_ts: new Date(startMs).toISOString(), scheduled_end_ts: new Date(endMs).toISOString(), scheduled_machine_name: machine } }));
  };

  const handleUnschedule = async (op) => {
    if (!op) return;
    const c = await Swal.fire({
      title: `Un-schedule ${op.wo_number} ${op.op_seq}?`,
      text: 'This will pull the operation off the Gantt. Status stays the same.',
      icon: 'question', showCancelButton: true,
      confirmButtonText: 'Yes, un-schedule', confirmButtonColor: '#dc2626',
    });
    if (!c.isConfirmed) return;
    try {
      await unscheduleOperation(op.id, customerId);
      setSelectedOp(null);
      await fetchAll();
    } catch (e) {
      Swal.fire('Error', e?.response?.data?.message || e.message, 'error');
    }
  };

  // Apply staged (un-saved) changes on top of the fetched ops until the user Saves
  const effectiveOps = useMemo(
    () => (grid.operations || []).map((o) => (pending[o.id] ? { ...o, ...pending[o.id] } : o)),
    [grid.operations, pending],
  );

  // Auto-extend the timeline to cover the furthest scheduled end, so long jobs are never cut off
  const effectiveDays = useMemo(() => {
    let maxEndMs = startDate.getTime() + days * MS_DAY;
    effectiveOps.forEach((o) => {
      if (o.scheduled_end_ts) {
        const e = new Date(o.scheduled_end_ts).getTime();
        if (e > maxEndMs) maxEndMs = e;
      }
    });
    return Math.min(60, Math.max(days, Math.ceil((maxEndMs - startDate.getTime()) / MS_DAY)));
  }, [effectiveOps, startDate, days]);

  const dayCells = useMemo(() => {
    const out = [];
    for (let i = 0; i < effectiveDays; i++) {
      const d = new Date(startDate.getTime() + i * MS_DAY);
      out.push({ key: istKey(d), label: istLabel(d), start: d });
    }
    return out;
  }, [startDate, effectiveDays]);

  // Sort WCs by workflow_order from Process Master
  const procOrder = useMemo(() => {
    const m = {};
    (processes || []).forEach((p) => { m[p.code] = p.workflow_order || 99; });
    return m;
  }, [processes]);
  const procColor = useMemo(() => {
    const m = {};
    (processes || []).forEach((p) => { m[p.code] = p.color || '#6b7280'; });
    return m;
  }, [processes]);

  const filteredWCs = useMemo(() => {
    let wcs = grid.work_centers || [];
    if (shopFilter !== 'ALL') wcs = wcs.filter((w) => w.shop_type === shopFilter);
    return wcs.slice().sort((a, b) => {
      const oa = procOrder[a.shop_type] || 99;
      const ob = procOrder[b.shop_type] || 99;
      return oa - ob || (a.sort_order || 0) - (b.sort_order || 0);
    });
  }, [grid.work_centers, shopFilter, procOrder]);

  // Group ops by work_center_id
  const opsByWC = useMemo(() => {
    const m = {};
    (grid.operations || []).forEach((o) => {
      if (!o.scheduled_start_ts || !o.scheduled_end_ts) return;
      if (!m[o.work_center_id]) m[o.work_center_id] = [];
      m[o.work_center_id].push(o);
    });
    return m;
  }, [grid.operations]);

  const unscheduledOps = useMemo(
    () => (grid.operations || []).filter((o) => !o.scheduled_start_ts),
    [grid.operations],
  );
  // Ops NOT visible on the current Gantt window (unscheduled, overdue, or beyond horizon) → show in tray so nothing disappears
  const trayOps = useMemo(
    () => effectiveOps.filter((o) => {
      if (!o.scheduled_start_ts) return true;                 // never scheduled
      const s = new Date(o.scheduled_start_ts).getTime();
      const e = o.scheduled_end_ts ? new Date(o.scheduled_end_ts).getTime() : s;
      const now = Date.now();
      if (s <= now && now <= e) return false;                 // currently running -> shown on the Gantt
      return e < now;                                         // finished/overdue in the past -> needs re-scheduling
    }),
    [effectiveOps],
  );


  // Phase 12 v15: rows are Machine Groups (cells), grouped by shop in workflow order.
  // For shops with no Machine Groups defined yet, fall back to a single placeholder row.
  const cellsForDisplay = useMemo(() => {
    let cells = (machineGroups || []).map(g => ({
      id: `cell:${g.name}`,
      code: g.name,
      name: `${(processes.find(p => p.code === g.shop_type)?.name) || g.shop_type} — ${g.name}`,
      shop_type: g.shop_type,
      machines: g.machines || [],
      machine_count: (g.machines || []).length,
    }));
    if (shopFilter !== 'ALL') cells = cells.filter(c => c.shop_type === shopFilter);

    // For shops with no Machine Groups, add a placeholder row per work_center so they're not hidden
    const shopsCovered = new Set(cells.map(c => c.shop_type));
    (grid.work_centers || [])
      .filter(w => !shopsCovered.has(w.shop_type) && (shopFilter === 'ALL' || w.shop_type === shopFilter))
      .forEach(w => cells.push({
        id: w.id,
        code: w.code,
        name: w.name,
        shop_type: w.shop_type,
        machines: [],
        machine_count: 0,
        _isWcFallback: true,
      }));

    return cells.sort((a, b) => {
      const oa = procOrder[a.shop_type] || 99;
      const ob = procOrder[b.shop_type] || 99;
      return oa - ob || a.code.localeCompare(b.code);
    });
  }, [machineGroups, grid.work_centers, shopFilter, processes, procOrder]);

  // Map ops to cells. If op has machine_name and that machine is in a cell's machines, attribute to that cell.
  // Unpinned ops (no machine_name) fall under the first cell of their shop.
  const opsByCellId = useMemo(() => {
    const m = {};
    cellsForDisplay.forEach(c => { m[c.id] = []; });
    // Build machine -> cellId index
    const machineToCellId = {};
    cellsForDisplay.forEach(c => {
      c.machines.forEach(mm => {
        const name = typeof mm === 'string' ? mm : mm?.name;
        if (name) machineToCellId[name] = c.id;
      });
    });
    // First cell of each shop (for unpinned ops)
    const firstCellOfShop = {};
    cellsForDisplay.forEach(c => { if (!firstCellOfShop[c.shop_type]) firstCellOfShop[c.shop_type] = c.id; });

    // Helper: cell-id -> shop_type (so we can validate cell matches op shop)
    const cellIdToShop = {};
    cellsForDisplay.forEach(c => { cellIdToShop[c.id] = c.shop_type; });

    effectiveOps.forEach(o => {
      if (!o.scheduled_start_ts || !o.scheduled_end_ts) return;
      const pinnedCellId = (o.scheduled_machine_name && machineToCellId[o.scheduled_machine_name])
                       || (o.machine_name && machineToCellId[o.machine_name]);
      // Only honor the pinned cell if its shop matches the op's shop;
      // otherwise the machine was misassigned — route to the op's correct shop's first cell.
      const target = (pinnedCellId && cellIdToShop[pinnedCellId] === o.work_center_shop_type)
                   ? pinnedCellId
                   : firstCellOfShop[o.work_center_shop_type];
      if (target && m[target]) m[target].push(o);
    });
    return m;
  }, [effectiveOps, cellsForDisplay]);

  // Rows to render: optionally hide cells that have no scheduled ops (like the Cockpit's Hide-unplanned)
  const renderCells = useMemo(
    () => (hideEmpty ? (cellsForDisplay || []).filter((cc) => (opsByCellId[cc.id] || []).length > 0) : cellsForDisplay),
    [cellsForDisplay, opsByCellId, hideEmpty],
  );

  // Flatten cells into rows; when a cell is expanded, add a sub-row per machine (machine-wise view)
  const flatRows = useMemo(() => {
    const rows = [];
    (renderCells || []).forEach((wc) => {
      const _cellExpanded = expanded.has(wc.id);
      const _cellOps = _cellExpanded ? [] : (opsByCellId[wc.id] || []);
      const _lanes = assignLanes(_cellOps);
      rows.push({ ...wc, _rowKind: 'cell', _ops: _cellOps, _laneByOp: _lanes.laneByOp, _laneCount: _lanes.laneCount, _rowKey: wc.id });
      if (expanded.has(wc.id)) {
        (wc.machines || []).forEach((mm) => {
          const name = typeof mm === 'string' ? mm : (mm && mm.name);
          if (!name) return;
          const mOps = (opsByCellId[wc.id] || []).filter((o) => (o.scheduled_machine_name || o.machine_name) === name);
          rows.push({ ...wc, _rowKind: 'machine', _machineName: name, _ops: mOps, _rowKey: wc.id + '::' + name });
        });
      }
    });
    return rows;
  }, [renderCells, opsByCellId, expanded]);

  // Capacity lookup: key = wc_id + day key
  const capByKey = useMemo(() => {
    const m = {};
    (capacity.daily || []).forEach((c) => {
      const k = istKey(new Date(c.day));
      m[`${c.work_center_id}|${k}`] = c;
    });
    return m;
  }, [capacity.daily]);

  // Cells of the selected op's shop, and machines within the chosen cell
  const cellsForOp = useMemo(() => {
    if (!selectedOp) return [];
    const shop = selectedOp.work_center_shop_type;
    return (cellsForDisplay || []).filter((cc) => !shop || cc.shop_type === shop);
  }, [selectedOp, cellsForDisplay]);
  const machinesForCell = useMemo(() => {
    const cell = cellsForOp.find((cc) => cc.id === formCell) || cellsForOp[0];
    if (!cell) return [];
    return (cell.machines || []).map((__m) => (typeof __m === 'string' ? __m : __m && __m.name)).filter(Boolean);
  }, [cellsForOp, formCell]);

  // Seed the panel form when a WO is selected
  useEffect(() => {
    if (!selectedOp) return;
    const cur = selectedOp.scheduled_machine_name || selectedOp.machine_name || '';
    const shop = selectedOp.work_center_shop_type;
    const cells = (cellsForDisplay || []).filter((cc) => !shop || cc.shop_type === shop);
    const nameOf = (__m) => (typeof __m === 'string' ? __m : __m && __m.name);
    const cell = cells.find((cc) => (cc.machines || []).some((__m) => nameOf(__m) === cur)) || cells[0];
    setFormCell(cell ? cell.id : '');
    setFormMachine(cur || (cell ? ((cell.machines || []).map(nameOf).filter(Boolean)[0] || '') : ''));
    const d = selectedOp.scheduled_start_ts ? new Date(selectedOp.scheduled_start_ts) : new Date();
    const pad = (n) => String(n).padStart(2, '0');
    setFormStart(`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
  }, [selectedOp, cellsForDisplay]);

  const handleScheduleFromPanel = async () => {
    if (!selectedOp || !formStart) return;
    const startMs = new Date(formStart).getTime();
    if (isNaN(startMs)) return;
    const perPiece = Number(selectedOp.planned_time_min || 0);
    const qty = Math.max(1, Number(selectedOp.planned_qty || 1));
    const totalMin = perPiece >= 60 ? perPiece : perPiece * qty;
    const endMs = startMs + (totalMin > 0 ? totalMin : 60) * 60000;
    setPending((p) => ({ ...p, [selectedOp.id]: { scheduled_start_ts: new Date(startMs).toISOString(), scheduled_end_ts: new Date(endMs).toISOString(), scheduled_machine_name: formMachine || null } }));
    setSelectedOp(null);
  };

  // Commit all staged drag/reschedule changes to the backend
  const savePlan = async () => {
    const entries = Object.entries(pending);
    if (!entries.length) return;
    try {
      setBusy(true);
      for (const [opId, body] of entries) {
        await scheduleOperation(opId, { customer_id: customerId, ...body });
      }
      setPending({});
      await fetchAll();
    } catch (err) { console.error('[Scheduler] save plan failed', err); }
    setBusy(false);
  };

  // Compute pixel-x and width for an op in a given row
  function opStyle(op, dayMs) {
    const startMs = new Date(op.scheduled_start_ts).getTime();
    const endMs = new Date(op.scheduled_end_ts).getTime();
    const dayVisStart = dayMs;                                 // 06:00 of this day (grid left edge)
    const dayVisEnd = dayMs + HOURS_PER_DAY_SHOWN * 3600_000;  // 22:00 of this day
    const segStart = Math.max(startMs, dayVisStart);
    const segEnd = Math.min(endMs, dayVisEnd);
    if (segEnd <= segStart) return null;                       // no visible part in this day
    const left = ((segStart - dayVisStart) / 3600_000) * PX_PER_HOUR;
    const width = ((segEnd - segStart) / 3600_000) * PX_PER_HOUR;
    const startsHere = startMs >= dayVisStart && startMs < dayVisEnd;
    const endsHere = endMs > dayVisStart && endMs <= dayVisEnd;
    return { left: Math.max(0, left), width: Math.max(10, width), startsHere, endsHere };
  }

  const shopOptions = ['ALL', ...processes.map(p => p.code).filter(Boolean)];

  return (
    <div className="scheduler-page pages" style={{ paddingTop: 32 }}>
      <div className="sched-header">
        <div>
          <h5 style={{ margin: 0 }}>Scheduler</h5>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            Plan operations across work centers · {dayCells[0]?.label} → {dayCells[dayCells.length - 1]?.label}
          </div>
        </div>
        <div className="sched-controls">
          <select value={shopFilter} onChange={(e) => setShopFilter(e.target.value)}>
            {shopOptions.map((s) => <option key={s} value={s}>{s === 'ALL' ? 'All shops' : s}</option>)}
          </select>
          <select value={includeHolidays ? 'include' : 'exclude'} onChange={(e) => setIncludeHolidays(e.target.value === 'include')} title="Exclude = skip Sundays + holidays (real calendar). Include = run 24/7 through them.">
            <option value="exclude">Holidays: Exclude</option>
            <option value="include">Holidays: Include</option>
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)} />
            Hide empty cells
          </label>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={3}>3 days</option>
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
          </select>
          {Object.keys(pending).length > 0 && (
            <>
              <button onClick={savePlan} disabled={busy}
                      style={{ padding: '6px 14px', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                {busy ? 'Saving…' : `Save (${Object.keys(pending).length})`}
              </button>
              <button onClick={() => setPending({})} disabled={busy}
                      style={{ padding: '6px 12px', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', background: '#fff', color: '#dc2626', border: '1px solid #dc2626', borderRadius: 6, cursor: 'pointer' }}>
                Discard
              </button>
            </>
          )}
          <button className="btn-secondary" onClick={fetchAll} disabled={loading || busy}>Refresh</button>
          <button className="btn-primary" onClick={runAutoSchedule} disabled={busy}>
            {busy ? 'Scheduling…' : 'Auto-Schedule'}
          </button>
        </div>
      </div>

      {unscheduledOps.length > 0 && (
        <div className="sched-banner">
          <strong>{unscheduledOps.length}</strong> operations un-scheduled.
          <button className="btn-link" onClick={runAutoSchedule} disabled={busy}>Auto-schedule them</button>
        </div>
      )}

      {trayOps.length > 0 && (
        <div className="sched-tray" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '0 0 12px' }}>
          <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 700 }}>Needs scheduling ({trayOps.length}) — drag onto a cell &rarr;</span>
          {trayOps.map((op) => {
            const s = op.scheduled_start_ts ? new Date(op.scheduled_start_ts).getTime() : null;
            const overdue = s !== null && s < Date.now();
            const bg = overdue ? '#fef2f2' : (s === null ? '#eef2ff' : '#fffbeb');
            const bd = overdue ? '#dc2626' : (s === null ? '#6366f1' : '#d97706');
            const fg = overdue ? '#991b1b' : (s === null ? '#3730a3' : '#92400e');
            const tag = overdue ? '⚠ OVERDUE' : (s === null ? 'unscheduled' : 'upcoming');
            return (
              <div key={op.id} draggable onDragStart={(e) => { setDragOp(op); e.dataTransfer.effectAllowed = 'move'; }} onDragEnd={() => setDragOp(null)}
                title={`${op.wo_number} ${op.op_seq} [${op.op_type || '-'}] — ${tag} — drag onto a cell`}
                style={{ padding: '6px 10px', background: bg, border: `1px dashed ${bd}`, borderRadius: 6, fontSize: 12, fontWeight: 700, color: fg, cursor: 'grab' }}>
                {overdue ? '⚠ ' : ''}{op.wo_number} {op.op_seq}{op.op_type ? ` · ${op.op_type}` : ''}
              </div>
            );
          })}
        </div>
      )}

      <div className="sched-grid-wrap">
        <div className="sched-grid" style={{ minWidth: 220 + days * (HOURS_PER_DAY_SHOWN * PX_PER_HOUR + 1) }}>
          {/* Day header */}
          <div className="sched-row sched-row-header">
            <div className="sched-row-label sched-row-label-header">Cell</div>
            {dayCells.map((d) => (
              <div key={d.key} className="sched-day-col sched-day-col-header" style={{ width: HOURS_PER_DAY_SHOWN * PX_PER_HOUR }}>
                <div className="sched-day-label">{d.label}</div>
                <div className="sched-hour-strip">
                  {Array.from({ length: HOURS_PER_DAY_SHOWN / 2 }).map((_, i) => (
                    <span key={i} style={{ width: PX_PER_HOUR * 2 }}>{String(6 + i * 2).padStart(2, '0')}:00</span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* WC rows */}
          {flatRows.map((wc) => (
            <div key={wc._rowKey} className="sched-row">
              <div className="sched-row-label"
                   style={wc._rowKind === 'machine' ? { paddingLeft: 26, background: '#fafafa' } : { cursor: 'pointer' }}
                   onClick={wc._rowKind === 'cell' ? () => setExpanded(prev => { const n = new Set(prev); if (n.has(wc.id)) n.delete(wc.id); else n.add(wc.id); return n; }) : undefined}
                   title={wc._rowKind === 'cell' ? 'Click to expand into machines' : undefined}>
                {wc._rowKind === 'cell' ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, color: '#6b7280', width: 10, textAlign: 'center' }}>{expanded.has(wc.id) ? '▾' : '▸'}</span>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: procColor[wc.shop_type] || '#6b7280' }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{wc.code}</div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>
                        {wc.machine_count > 0 ? `${wc.machine_count} machine${wc.machine_count !== 1 ? 's' : ''} · click to expand` : wc.name}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#9ca3af' }} />
                    <span style={{ fontWeight: 600, fontSize: 12, color: '#374151', fontFamily: 'monospace' }}>{wc._machineName}</span>
                  </div>
                )}
              </div>
              {dayCells.map((d) => {
                // Capacity from the CELL's real machine count (work_center count is unreliable / often 1)
                const _dayMid = d.start.getTime() - 6 * 3600000;   // IST midnight of this day
                const _dayEnd = _dayMid + 24 * 3600000;
                let _schedH = 0;
                (opsByCellId[wc.id] || []).forEach((o) => {
                  if (!o.scheduled_start_ts || !o.scheduled_end_ts) return;
                  const os = new Date(o.scheduled_start_ts).getTime();
                  const oe = new Date(o.scheduled_end_ts).getTime();
                  _schedH += Math.max(0, Math.min(oe, _dayEnd) - Math.max(os, _dayMid)) / 3600000;
                });
                const _mc = wc.machine_count || (wc.machines ? wc.machines.length : 1) || 1;
                const _capH = _mc * 22;   // ~22 working h per machine per day (24h minus breaks)
                const util = _capH > 0 ? Math.round((_schedH / _capH) * 100) : 0;
                const cap = _schedH > 0 ? { scheduled_hours: _schedH, capacity_hours: _capH } : null;
                const capColor = util > 100 ? '#fee2e2' : util > 80 ? '#fef3c7' : util > 0 ? '#dcfce7' : '#f9fafb';
                const dayMs = d.start.getTime();
                const dayVisStart = dayMs;
                const dayVisEnd = dayMs + HOURS_PER_DAY_SHOWN * 3600000;
                const dayOps = (wc._ops || []).filter((o) => {
                  if (!o.scheduled_start_ts || !o.scheduled_end_ts) return false;
                  const s = new Date(o.scheduled_start_ts).getTime();
                  const e = new Date(o.scheduled_end_ts).getTime();
                  return s < dayVisEnd && e > dayVisStart;
                });
                return (
                  <div
                    key={d.key}
                    className="sched-day-cell"
                    style={{ width: HOURS_PER_DAY_SHOWN * PX_PER_HOUR, background: capColor, minHeight: (wc._rowKind === 'cell' && wc._laneCount > 1) ? Math.max(72, wc._laneCount * 22 + 8) : undefined }}
                    onDragOver={(e) => { if (dragOp) e.preventDefault(); }}
                    onDrop={(e) => handleDropOnCell(e, wc, d)}
                  >
                    {cap && wc._rowKind !== 'machine' && (
                      <div className="sched-cap-badge" title={`${cap.scheduled_hours.toFixed(1)}h scheduled / ${cap.capacity_hours}h capacity`}>
                        {util}%
                      </div>
                    )}
                    {dayOps.map((op) => {
                      const s = opStyle(op, dayMs);
                      if (!s) return null;
                      const color = jobColor(op.wo_number || op.op_seq || op.id);
                      const _lane = (wc._rowKind === 'cell' && wc._laneByOp) ? (wc._laneByOp.get(op.id) || 0) : 0;
                      return (
                        <div
                          key={op.id}
                          draggable
                          onDragStart={(e) => { setDragOp(op); e.dataTransfer.effectAllowed = 'move'; }}
                          onDragEnd={() => setDragOp(null)}
                          className={`sched-op-bar ${selectedOp?.id === op.id ? 'sched-op-bar-selected' : ''}`}
                          style={{
                            left: s.left, width: s.width,
                            background: color,
                            borderColor: color,
                            top: wc._rowKind === 'cell' ? (3 + _lane * 22) : undefined,
                            height: wc._rowKind === 'cell' ? 18 : undefined,
                            borderTopLeftRadius: s.startsHere ? undefined : 0,
                            borderBottomLeftRadius: s.startsHere ? undefined : 0,
                            borderTopRightRadius: s.endsHere ? undefined : 0,
                            borderBottomRightRadius: s.endsHere ? undefined : 0,
                            opacity: s.startsHere ? 1 : 0.8,
                          }}
                          onMouseEnter={(e) => setBarTip({ op, x: e.clientX, y: e.clientY })}
                          onMouseMove={(e) => setBarTip((t) => (t ? { ...t, x: e.clientX, y: e.clientY } : t))}
                          onMouseLeave={() => setBarTip(null)}
                          onClick={() => setSelectedOp(op)}
                        >
                          {s.startsHere && (
                          <span className="sched-op-label">
                            {op.wo_number} {op.op_seq}
                            {op.op_type && <span className="sched-op-type-chip">{op.op_type}</span>}
                          </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}

          {renderCells.length === 0 && !loading && (
            <div style={{ padding: 28, color: '#9ca3af', textAlign: 'center' }}>
              No work centers configured for {shopFilter === 'ALL' ? 'this customer' : shopFilter}.
            </div>
          )}
        </div>
      </div>

      {barTip && (() => {
        const o = barTip.op;
        const col = jobColor(o.wo_number || o.op_seq || o.id);
        const fmtDT = (iso) => { if (!iso) return '—'; const d = new Date(iso); const p = (n) => String(n).padStart(2, '0'); return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`; };
        const right = barTip.x > window.innerWidth - 320;
        return (
          <div style={{ position: 'fixed', left: right ? barTip.x - 312 : barTip.x + 16, top: Math.max(10, Math.min(barTip.y + 16, window.innerHeight - 220)),
            width: 290, background: '#fff', border: '1px solid #d1d5db', borderRadius: 8,
            boxShadow: '0 10px 28px rgba(0,0,0,0.16), 0 2px 6px rgba(0,0,0,0.08)', zIndex: 3000, pointerEvents: 'none',
            fontSize: 12, color: '#111827', borderLeft: `4px solid ${col}` }}>
            <div style={{ padding: '9px 12px', borderBottom: '1px solid #f1f5f9', background: '#fafafa', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTopRightRadius: 6 }}>
              <span style={{ fontWeight: 800, fontSize: 13 }}>{o.wo_number} · {o.op_seq}</span>
              {o.op_type && <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', background: col, padding: '2px 7px', borderRadius: 999 }}>{o.op_type}</span>}
            </div>
            <div style={{ padding: '9px 12px', display: 'grid', gridTemplateColumns: '78px 1fr', rowGap: 5, columnGap: 8 }}>
              <span style={{ color: '#6b7280', fontWeight: 600 }}>Operation</span>
              <span style={{ fontWeight: 700 }}>{o.op_name || '—'}</span>
              <span style={{ color: '#6b7280', fontWeight: 600 }}>Machine</span>
              <span style={{ fontWeight: 700, color: '#1e3a8a' }}>{o.scheduled_machine_name || o.machine_name || '—'}</span>
              <span style={{ color: '#6b7280', fontWeight: 600 }}>Qty</span>
              <span style={{ fontWeight: 700 }}>{(o.actual_qty_good != null ? o.actual_qty_good + ' / ' : '')}{o.planned_qty || '—'}</span>
              <span style={{ color: '#6b7280', fontWeight: 600 }}>Start</span>
              <span style={{ fontWeight: 600 }}>{fmtDT(o.scheduled_start_ts)}</span>
              <span style={{ color: '#6b7280', fontWeight: 600 }}>End</span>
              <span style={{ fontWeight: 600 }}>{fmtDT(o.scheduled_end_ts)}</span>
              <span style={{ color: '#6b7280', fontWeight: 600 }}>Status</span>
              <span style={{ fontWeight: 700 }}>{o.status || '—'}</span>
            </div>
          </div>
        );
      })()}

      {selectedOp && (
        <div className="sched-side-panel">
          <div className="sched-side-header">
            <div>
              <div style={{ fontSize: 11, color: '#9ca3af', letterSpacing: 0.5 }}>WORK ORDER</div>
              <div style={{ fontSize: 17, fontWeight: 800 }}>{selectedOp.wo_number}</div>
            </div>
            <button onClick={() => setSelectedOp(null)} className="sched-side-close">×</button>
          </div>
          <div className="sched-side-row">
            <span>Operation</span><strong>{selectedOp.op_seq} — {selectedOp.op_name}{selectedOp.op_type && ` [${selectedOp.op_type}]`}</strong>
          </div>
          <div className="sched-side-row">
            <span>Status</span><strong>{selectedOp.status}</strong>
          </div>
          <div className="sched-side-row">
            <span>Work Center</span><strong>{selectedOp.work_center_code}</strong>
          </div>
          <div className="sched-side-row">
            <span>Cell</span>
            <select value={formCell} onChange={(e) => {
                const cid = e.target.value; setFormCell(cid);
                const cell = cellsForOp.find((cc) => cc.id === cid);
                const ms = cell ? (cell.machines || []).map((__m) => (typeof __m === 'string' ? __m : __m && __m.name)).filter(Boolean) : [];
                setFormMachine(ms[0] || '');
              }} style={{ padding: '5px 8px', fontSize: 13, fontWeight: 600, border: '1px solid #d1d5db', borderRadius: 6, maxWidth: 200 }}>
              {cellsForOp.map((cc) => <option key={cc.id} value={cc.id}>{cc.code}</option>)}
            </select>
          </div>
          <div className="sched-side-row">
            <span>Machine</span>
            <select value={formMachine} onChange={(e) => setFormMachine(e.target.value)} style={{ padding: '5px 8px', fontSize: 13, fontWeight: 600, border: '1px solid #d1d5db', borderRadius: 6, maxWidth: 200 }}>
              {machinesForCell.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="sched-side-row">
            <span>Current</span>
            <strong>{selectedOp.scheduled_start_ts ? `${fmtTime(selectedOp.scheduled_start_ts)} → ${fmtTime(selectedOp.scheduled_end_ts)}` : 'Un-scheduled'}</strong>
          </div>
          <div className="sched-side-row">
            <span>Start date/time</span>
            <input type="datetime-local" value={formStart} onChange={(e) => setFormStart(e.target.value)} style={{ padding: '5px 8px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6 }} />
          </div>
          <div className="sched-side-row">
            <span>Planned Qty</span><strong>{selectedOp.planned_qty || '—'}</strong>
          </div>
          <div className="sched-side-row">
            <span>Planned Time</span>
            <strong>{(() => {
              const ptm = Number(selectedOp.planned_time_min || 0);
              const qty = Number(selectedOp.planned_qty || 0);
              if (!ptm) return '—';
              const totalMin = ptm >= 60 ? ptm : ptm * qty;
              const perMin = qty > 0 ? totalMin / qty : totalMin;
              const totalStr = totalMin >= 90 ? (totalMin / 60).toFixed(1) + ' h' : Math.round(totalMin) + ' min';
              const perStr = perMin >= 60
                ? (Math.floor(perMin / 60) + ':' + String(Math.round(perMin % 60)).padStart(2, '0') + ' h/pc')
                : (perMin.toFixed(1) + ' min/pc');
              return qty > 0 ? (totalStr + ' (' + perStr + ' × ' + qty + ')') : totalStr;
            })()}</strong>
          </div>
          <div className="sched-side-actions">
            <button className="btn-primary" onClick={handleScheduleFromPanel} disabled={busy}>
              {selectedOp.scheduled_start_ts ? 'Reschedule' : 'Schedule'}
            </button>
            <button className="btn-danger" onClick={() => handleUnschedule(selectedOp)}>
              Un-schedule
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
